// Cross-platform file download helper.
//
// In a normal browser this performs the standard anchor-download. When the page
// is loaded inside the company's React Native WebView (which has no download
// manager, so blob:/<a download> are silently ignored) the file bytes are sent
// to native via the existing window.ReactNativeWebView bridge for saving.

/** Convert a Blob to a base64 string (without the leading "data:<mime>;base64," prefix). */
const blobToBase64 = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result as string; // "data:<mime>;base64,XXXX"
      resolve(result.split(',')[1] ?? '');
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

/** True when running inside the React Native WebView wrapper. */
export const isReactNativeWebView = (): boolean =>
  typeof window !== 'undefined' && !!window.ReactNativeWebView;

/** Post a base64-encoded file payload to native over the bridge. */
const postNativeFile = async (
  action: string,
  blob: Blob,
  fileName: string,
  mimeType: string,
): Promise<void> => {
  const data = await blobToBase64(blob);
  window.ReactNativeWebView!.postMessage(
    JSON.stringify({ action, fileName, mimeType, data }),
  );
};

/**
 * Saves a Blob to the device. Inside the RN WebView the bytes are sent to native
 * via the DOWNLOAD_FILE bridge message; on web it falls back to the standard
 * anchor-download.
 */
export const downloadBlob = async (
  blob: Blob,
  fileName: string,
  mimeType: string,
): Promise<void> => {
  if (isReactNativeWebView()) {
    await postNativeFile('DOWNLOAD_FILE', blob, fileName, mimeType);
    return;
  }

  // Web fallback — existing behaviour.
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

/**
 * Asks native to print a Blob (PRINT_FILE bridge message). Returns true when the
 * request was handed to native; returns false on web so the caller can run its
 * own browser-print fallback (window.print()/iframe), which native can't do.
 */
export const printBlob = async (
  blob: Blob,
  fileName: string,
  mimeType: string,
): Promise<boolean> => {
  if (isReactNativeWebView()) {
    await postNativeFile('PRINT_FILE', blob, fileName, mimeType);
    return true;
  }
  return false;
};

export interface ShareOptions {
  url: string;
  title?: string;
  text?: string;
  /** Optional file (e.g. a QR image) to attach to the share. */
  blob?: Blob | null;
  fileName?: string;
  mimeType?: string;
}

/**
 * Shares content via the native share sheet (SHARE bridge message) inside the RN
 * WebView, or the Web Share API on web. Returns true when a share mechanism was
 * available, false when none exists so the caller can fall back (e.g. copy link).
 */
export const shareContent = async (opts: ShareOptions): Promise<boolean> => {
  if (isReactNativeWebView()) {
    const data = opts.blob ? await blobToBase64(opts.blob) : undefined;
    window.ReactNativeWebView!.postMessage(
      JSON.stringify({
        action: 'SHARE',
        url: opts.url,
        title: opts.title,
        text: opts.text,
        fileName: opts.fileName,
        mimeType: opts.mimeType,
        data,
      }),
    );
    return true;
  }

  if (typeof navigator !== 'undefined' && navigator.share) {
    try {
      await navigator.share({ title: opts.title, text: opts.text, url: opts.url });
    } catch (err) {
      // user cancelled — no-op
    }
    return true;
  }

  return false;
};
